import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCurrency } from '../hooks/useCurrency';
import api from '../utils/api';
import { toast } from 'react-toastify';

// Card payment via NMI Collect.js. The card fields below are secure iframes
// hosted by NMI — raw card data never touches our frontend or backend. On
// submit, Collect.js returns a one-time token which we POST to /orders/:id/pay,
// where the backend runs the actual sale through NMI's Direct Post API.

const COLLECT_JS_SRC = 'https://secure.nmi.com/token/Collect.js';
const NMI_TOKENIZATION_KEY = process.env.REACT_APP_NMI_TOKENIZATION_KEY;

// Load Collect.js once, with the public tokenization key on the script tag
// (Collect.js reads it from there). Resolves when window.CollectJS is ready.
function loadCollectJs() {
  return new Promise((resolve, reject) => {
    if (window.CollectJS) return resolve(window.CollectJS);
    const existing = document.querySelector(`script[src="${COLLECT_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.CollectJS));
      existing.addEventListener('error', () => reject(new Error('Collect.js failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = COLLECT_JS_SRC;
    script.async = true;
    script.setAttribute('data-tokenization-key', NMI_TOKENIZATION_KEY || '');
    script.onload = () => resolve(window.CollectJS);
    script.onerror = () => reject(new Error('Collect.js failed to load'));
    document.head.appendChild(script);
  });
}

export default function NmiPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fmt } = useCurrency();

  const [order, setOrder]       = useState(null);
  const [error, setError]       = useState('');
  const [ready, setReady]       = useState(false);   // Collect.js fields mounted
  const [paying, setPaying]     = useState(false);
  const configuredRef = useRef(false);

  // ── Load the order (and bounce if it's already paid) ──────────────
  const loadOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      if (['paid', 'processing', 'shipped', 'delivered'].includes(data.status)) {
        navigate(`/thank-you/${id}`, { replace: true });
        return;
      }
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load order');
    }
  }, [id, navigate]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // ── Mount Collect.js secure fields once we have an order ──────────
  useEffect(() => {
    if (!order || !NMI_TOKENIZATION_KEY || configuredRef.current) return;
    configuredRef.current = true;

    let cancelled = false;
    loadCollectJs()
      .then((CollectJS) => {
        if (cancelled || !CollectJS) return;
        CollectJS.configure({
          variant: 'inline',
          // Styling for the text INSIDE the NMI-hosted iframes.
          styleSniffer: true,
          fields: {
            ccnumber: { selector: '#nmi-ccnumber', placeholder: '0000 0000 0000 0000' },
            ccexp:    { selector: '#nmi-ccexp',    placeholder: 'MM / YY' },
            cvv:      { selector: '#nmi-cvv',       placeholder: 'CVV' },
          },
          fieldsAvailableCallback: () => { if (!cancelled) setReady(true); },
          // Fires after startPaymentRequest() once NMI returns a token.
          callback: (response) => { submitToken(response.token); },
        });
      })
      .catch((err) => { if (!cancelled) setError(err.message); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // ── Hand the one-time token to our backend to run the sale ────────
  const submitToken = async (token) => {
    if (!token) {
      setPaying(false);
      toast.error('Could not read card details. Please check the fields and try again.');
      return;
    }
    try {
      const { data } = await api.post(`/orders/${id}/pay`, { paymentToken: token });
      if (data.status === 'paid' || data.alreadyPaid) {
        toast.success('Payment approved!');
        navigate(`/thank-you/${id}`, { replace: true });
      } else {
        toast.error('Payment could not be completed.');
        setPaying(false);
      }
    } catch (err) {
      // 402 = declined (responsetext passed through), others = gateway/validation.
      toast.error(err.response?.data?.error || 'Payment failed. Please try again.');
      setPaying(false);
    }
  };

  const handlePay = (e) => {
    e.preventDefault();
    if (!window.CollectJS || !ready) return;
    setPaying(true);
    // Triggers tokenization; the configured `callback` resolves with the token.
    window.CollectJS.startPaymentRequest();
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main id="main-content" tabIndex={-1} className="flex-1 max-w-lg mx-auto px-4 py-16 w-full text-center">
          <h1 className="text-2xl font-bold mb-3">Payment</h1>
          <p className="text-red-500 mb-6">{error}</p>
          <Link to="/account" className="btn-primary inline-block px-6 py-2">View my orders</Link>
        </main>
        <Footer />
      </div>
    );
  }

  // Processor not configured on this build — fall back to the existing
  // "we'll follow up with instructions" success screen rather than a broken form.
  if (!NMI_TOKENIZATION_KEY) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-lg mx-auto px-4 py-16 w-full text-center">
          <h1 className="text-2xl font-bold mb-3">Order placed</h1>
          <p className="text-gray-600 mb-6">
            Online card payment is being onboarded. Our team will follow up with
            secure payment instructions to complete your order.
          </p>
          <Link to={`/order-success/${id}`} className="btn-primary inline-block px-6 py-2">
            Continue
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-lg mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-1">Card Payment</h1>
        <p className="text-sm text-gray-500 mb-6">
          Secured by NMI. Your card details are entered directly into the
          processor and never touch our servers.
        </p>

        <form onSubmit={handlePay} className="card space-y-4">
          {order && (
            <div className="flex justify-between items-baseline border-b pb-3 mb-1">
              <span className="text-gray-600">Order total</span>
              <span className="text-2xl font-bold">{fmt(order.totalInCents)}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
            <div id="nmi-ccnumber" className="input-field h-12 flex items-center" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
              <div id="nmi-ccexp" className="input-field h-12 flex items-center" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
              <div id="nmi-cvv" className="input-field h-12 flex items-center" />
            </div>
          </div>

          <button
            type="submit"
            disabled={!ready || paying}
            className="btn-primary w-full py-3 disabled:opacity-50"
          >
            {paying ? 'Processing…' : !ready ? 'Loading secure form…' : `Pay ${order ? fmt(order.totalInCents) : ''}`}
          </button>

          <p className="text-xs text-gray-400 text-center pt-1">
            For laboratory research use only.
          </p>
        </form>
      </main>
      <Footer />
    </div>
  );
}
